import React from 'react'
import { Component } from 'reflux'
import { PurchaseOrderStore, PurchaseOrderStoreActions } from '../../stores/purchaseOrderStore'
import {
  Paper,
  CircularProgress,
  Typography,
  Box,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  TableContainer,
  TableSortLabel,
  Button
} from '@material-ui/core'

const SELECTED_ROW_BG = 'rgba(40, 208, 148, 0.18)'
const BELOW_MIN_COLOR = '#c62828'

export default class PurchaseOrderOverview extends Component {
  constructor(props) {
    super(props)
    this.stores = [PurchaseOrderStore]
    this.storeKeys = ['supplierOverview', 'purchaseOrderOverviewTotals', 'purchaseOrderOverviewLoading']
    this.state = {
      selectedSupplierId: null,
      supplierNameFilter: '',
      orderBy: 'totalDKK',
      order: 'desc'
    }
  }

  componentDidMount() {
    PurchaseOrderStoreActions.getPurchaseOrderOverview()
  }

  toArray = v => (Array.isArray(v) ? v : v && typeof v === 'object' ? Object.values(v) : [])

  formatDkk = v => Number(v || 0).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  formatDate = s => {
    if (!s) return ''
    const d = new Date(s)
    if (isNaN(d)) return ''
    return d.toLocaleDateString('da-DK')
  }

  applyFilters = list => {
    const q = this.state.supplierNameFilter.trim().toLowerCase()
    if (!q) return list
    return list.filter(s =>
      String(s.supplierName ?? '')
        .toLowerCase()
        .includes(q)
    )
  }

  getValue = (row, key) => {
    switch (key) {
      case 'supplierID':
        return Number(row.supplierID) || 0
      case 'supplierName':
        return row.supplierName || ''
      case 'orderlineCount':
        return Number(row.orderlineCount) || 0
      case 'totalDKK':
        return Number(row.totalDKK) || 0
      case 'newestPurchaseOrderNumber':
        return String(row.newestPurchaseOrderNumber || '')
      case 'newestPurchaseOrderDate':
        return row.newestPurchaseOrderDate ? new Date(row.newestPurchaseOrderDate) : new Date(0)
      case 'oldestOrderlineTimeInsert':
        return row.oldestOrderlineTimeInsert ? new Date(row.oldestOrderlineTimeInsert) : new Date(0)
      case 'minOrderAmountDkk':
        return Number(row.minOrderAmountDkk) || 0
      default:
        return row[key]
    }
  }

  getComparator = (order, orderBy) => (a, b) => {
    const av = this.getValue(a, orderBy)
    const bv = this.getValue(b, orderBy)
    let cmp = 0
    if (av instanceof Date && bv instanceof Date) {
      cmp = av.getTime() - bv.getTime()
    } else if (typeof av === 'number' && typeof bv === 'number') {
      cmp = av - bv
    } else {
      cmp = String(av).localeCompare(String(bv), 'da', { numeric: true, sensitivity: 'base' })
    }
    return order === 'desc' ? -cmp : cmp
  }

  handleRequestSort = property => {
    const isAsc = this.state.orderBy === property && this.state.order === 'asc'
    this.setState({ order: isAsc ? 'desc' : 'asc', orderBy: property })
  }

  handleRowClick = supplierID => {
    this.setState(prev => ({
      selectedSupplierId: prev.selectedSupplierId === supplierID ? null : supplierID
    }))
  }

  handleCreatePO = supplier => {
    if (!supplier) return
    const orderlines = this.toArray(supplier.orderlines)
    if (!orderlines.length) return
    PurchaseOrderStoreActions.createPurchaseOrderFromOverview({
      supplierID: supplier.supplierID,
      orderlines
    })
  }

  renderRightPane = supplier => {
    const lines = this.toArray(supplier?.orderlines)
    const belowMin = supplier ? Number(supplier.totalDKK || 0) < Number(supplier.minOrderAmountDkk || 0) : false

    return (
      <Paper style={{ height: '75vh', display: 'flex', flexDirection: 'column' }}>
        <Box p={2} display='flex' justifyContent='space-between' alignItems='center'>
          <Box>
            <Typography variant='h6'>{supplier ? supplier.supplierName : 'Ingen valgt'}</Typography>
            {supplier && (
              <Box display='flex' gridGap={16} mt={0.5}>
                <Typography variant='body2'>Linjer: {supplier.orderlineCount}</Typography>
                <Typography variant='body2' style={{ color: belowMin ? BELOW_MIN_COLOR : undefined, fontWeight: belowMin ? 700 : undefined }}>
                  Total: {this.formatDkk(supplier.totalDKK)} DKK
                </Typography>
                <Typography variant='body2' style={{ color: belowMin ? BELOW_MIN_COLOR : undefined, fontWeight: belowMin ? 700 : undefined }}>
                  Min. ordre: {this.formatDkk(supplier.minOrderAmountDkk || 0)} DKK
                </Typography>
              </Box>
            )}
          </Box>
          <Button
            variant='contained'
            color='primary'
            size='small'
            disabled={!supplier || !lines.length}
            onClick={() => this.handleCreatePO(supplier)}
          >
            Opret indkøbsordre
          </Button>
        </Box>
        <TableContainer style={{ flex: 1, maxHeight: '100%', overflow: 'auto' }}>
          <Table size='small' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 110 }}>OrderID</TableCell>
                <TableCell style={{ width: 130 }}>OrderlineID</TableCell>
                <TableCell style={{ width: 110 }}>ProduktID</TableCell>
                <TableCell>Titel</TableCell>
                <TableCell style={{ width: 120, textAlign: 'right' }}>Pris</TableCell>
                <TableCell style={{ width: 130 }}>Dato</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {supplier &&
                lines.map(l => (
                  <TableRow key={l.OrderlineID} hover>
                    <TableCell>{l.OrderID}</TableCell>
                    <TableCell>{l.OrderlineID}</TableCell>
                    <TableCell>{l.ProductID}</TableCell>
                    <TableCell>{l.productTitle}</TableCell>
                    <TableCell style={{ textAlign: 'right' }}>{this.formatDkk(l.productPrice)}</TableCell>
                    <TableCell>{this.formatDate(l.timeInsert)}</TableCell>
                  </TableRow>
                ))}
              {!supplier && (
                <TableRow>
                  <TableCell colSpan={6} align='center'>
                    Vælg en leverandør til venstre
                  </TableCell>
                </TableRow>
              )}
              {supplier && lines.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align='center'>
                    Ingen orderlinjer
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    )
  }

  render() {
    const loading = this.state.purchaseOrderOverviewLoading
    if (loading) {
      return (
        <Box display='flex' justifyContent='center' alignItems='center' minHeight={200}>
          <CircularProgress />
        </Box>
      )
    }

    const raw = this.toArray(this.state.supplierOverview || this.state.purchaseOrderOverviewTotals)
    const mapped = raw.map(s => ({
      supplierID: s.supplierID,
      supplierName: s.supplierName,
      orderlineCount: s.orderlineCount,
      totalDKK: s.totalDKK,
      newestPurchaseOrderNumber: s.newestPurchaseOrderNumber,
      newestPurchaseOrderDate: s.newestPurchaseOrderDate,
      oldestOrderlineTimeInsert: s.oldestOrderlineTimeInsert,
      isDropShip: s.isDropShip,
      minOrderAmountDkk: s.minOrderAmountDkk,
      orderlines: s.orderlines || []
    }))

    const filtered = this.applyFilters(mapped)
    const suppliers = filtered.sort(this.getComparator(this.state.order, this.state.orderBy))
    const selected = suppliers.find(s => s.supplierID === this.state.selectedSupplierId) || null
    const { order, orderBy, supplierNameFilter } = this.state

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper style={{ height: '75vh', display: 'flex', flexDirection: 'column' }}>
            <Box p={2} display='flex' justifyContent='space-between' alignItems='center'>
              <Typography variant='h6'>Leverandører</Typography>
              <TextField
                value={supplierNameFilter}
                onChange={e => this.setState({ supplierNameFilter: e.target.value })}
                size='small'
                variant='outlined'
                placeholder='Søg leverandør'
              />
            </Box>
            <TableContainer style={{ flex: 1, maxHeight: '100%', overflow: 'auto' }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ whiteSpace: 'nowrap' }} sortDirection={orderBy === 'supplierName' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'supplierName'}
                        direction={orderBy === 'supplierName' ? order : 'asc'}
                        onClick={() => this.handleRequestSort('supplierName')}
                      >
                        Leverandør
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      style={{ width: 120, textAlign: 'left', whiteSpace: 'nowrap' }}
                      sortDirection={orderBy === 'orderlineCount' ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'orderlineCount'}
                        direction={orderBy === 'orderlineCount' ? order : 'asc'}
                        onClick={() => this.handleRequestSort('orderlineCount')}
                      >
                        Linjer
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      style={{ width: 140, textAlign: 'right', whiteSpace: 'nowrap' }}
                      sortDirection={orderBy === 'totalDKK' ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'totalDKK'}
                        direction={orderBy === 'totalDKK' ? order : 'asc'}
                        onClick={() => this.handleRequestSort('totalDKK')}
                      >
                        Total DKK
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      style={{ width: 120, textAlign: 'right', whiteSpace: 'nowrap' }}
                      sortDirection={orderBy === 'minOrderAmountDkk' ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'minOrderAmountDkk'}
                        direction={orderBy === 'minOrderAmountDkk' ? order : 'asc'}
                        onClick={() => this.handleRequestSort('minOrderAmountDkk')}
                      >
                        Min. ordre
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      style={{ width: 120, textAlign: 'right', whiteSpace: 'nowrap' }}
                      sortDirection={orderBy === 'orderlineCount' ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'isDropShip'}
                        direction={orderBy === 'isDropShip' ? order : 'asc'}
                        onClick={() => this.handleRequestSort('isDropShip')}
                      >
                        Dropship
                      </TableSortLabel>
                    </TableCell>
                    <TableCell style={{ width: 130, whiteSpace: 'nowrap' }} sortDirection={orderBy === 'newestPurchaseOrderNumber' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'newestPurchaseOrderNumber'}
                        direction={orderBy === 'newestPurchaseOrderNumber' ? order : 'asc'}
                        onClick={() => this.handleRequestSort('newestPurchaseOrderNumber')}
                      >
                        Seneste PO
                      </TableSortLabel>
                    </TableCell>
                    <TableCell style={{ width: 130, whiteSpace: 'nowrap' }} sortDirection={orderBy === 'newestPurchaseOrderDate' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'newestPurchaseOrderDate'}
                        direction={orderBy === 'newestPurchaseOrderDate' ? order : 'asc'}
                        onClick={() => this.handleRequestSort('newestPurchaseOrderDate')}
                      >
                        Sidst bestilt
                      </TableSortLabel>
                    </TableCell>
                    <TableCell style={{ width: 130, whiteSpace: 'nowrap' }} sortDirection={orderBy === 'oldestOrderlineTimeInsert' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'oldestOrderlineTimeInsert'}
                        direction={orderBy === 'oldestOrderlineTimeInsert' ? order : 'asc'}
                        onClick={() => this.handleRequestSort('oldestOrderlineTimeInsert')}
                      >
                        Ældste ordre
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suppliers.map(s => {
                    const selectedRow = this.state.selectedSupplierId === s.supplierID
                    const belowMin = Number(s.totalDKK || 0) < Number(s.minOrderAmountDkk || 0)
                    return (
                      <TableRow
                        key={s.supplierID}
                        hover
                        selected={selectedRow}
                        onClick={() => this.handleRowClick(s.supplierID)}
                        style={{ cursor: 'pointer', backgroundColor: selectedRow ? SELECTED_ROW_BG : undefined }}
                      >
                        <TableCell>{s.supplierName}</TableCell>
                        <TableCell style={{ textAlign: 'left' }}>{s.orderlineCount}</TableCell>
                        <TableCell
                          style={{
                            textAlign: 'right',
                            color: belowMin ? BELOW_MIN_COLOR : undefined,
                            fontWeight: belowMin ? 700 : undefined
                          }}
                        >
                          {this.formatDkk(s.totalDKK)}
                        </TableCell>
                        <TableCell
                          style={{
                            textAlign: 'right',
                            color: belowMin ? BELOW_MIN_COLOR : undefined,
                            fontWeight: belowMin ? 700 : undefined
                          }}
                        >
                          {this.formatDkk(s.minOrderAmountDkk || 0)}
                        </TableCell>
                        <TableCell>{s.isDropShip ? 'Ja' : 'Nej'}</TableCell>
                        <TableCell>{s.newestPurchaseOrderNumber || ''}</TableCell>
                        <TableCell>{this.formatDate(s.newestPurchaseOrderDate)}</TableCell>
                        <TableCell>{this.formatDate(s.oldestOrderlineTimeInsert)}</TableCell>
                      </TableRow>
                    )
                  })}
                  {suppliers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align='center'>
                        Ingen leverandørdata
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          {this.renderRightPane(selected)}
        </Grid>
      </Grid>
    )
  }
}
